import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetBlogTags } from "../../hooks/blog-tags/useGetBlogTags";
import { useDeleteBlogTag } from "../../hooks/blog-tags/useDeleteBlogTag";
import { useDeleteAllBlogTags } from "../../hooks/blog-tags/useDeleteAllBlogTags";
import { useRestoreAllBlogTags } from "../../hooks/blog-tags/useRestoreAllBlogTags";
import { useForceDeleteAllBlogTags } from "../../hooks/blog-tags/useForceDeleteAllBlogTags";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import BlogTagsTableRow from "./BlogTagsTableRow";
import { BlogTagsTableColumns } from "./BlogTagsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const BlogTagsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("blog-tags.view"),
            create: has("blog-tags.create"),
            edit: has("blog-tags.update"),
            delete: has("blog-tags.delete"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const [trashedFilter, setTrashedFilter] = useState("");

    const { deleteBlogTagMutate, isPending: isDelete } = useDeleteBlogTag();
    const { deleteAllBlogTagsMutate, isPending: isDeleteAll } = useDeleteAllBlogTags();
    const { restoreAllBlogTagsMutate, isPending: isRestoreAll } = useRestoreAllBlogTags();
    const { forceDeleteAllBlogTagsMutate, isPending: isForceDeleteAll } = useForceDeleteAllBlogTags();

    const {
        page, perPage, search, order, selectedIds, menuAnchorEl,
        setSearch, handleRequestSort, handleRowCheckboxClick, handleSelectAllClick,
        handleMenuOpen, handleMenuClose, handleEdit, handleMenuDelete, handleMenuRestore,
        handleBulkDelete, handleBulkRestore, handlePageChange, handlePerPageChange,
    } = useTableActions({
        basePath: "/blog/tags",
        deleteSingle: deleteBlogTagMutate,
        deleteAll: deleteAllBlogTagsMutate,
        restoreSingle: (id, options) => restoreAllBlogTagsMutate({ items: [id] }, options),
        restoreAll: restoreAllBlogTagsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllBlogTagsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllBlogTagsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page, per_page: perPage, search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'created_at', sort_order: order,
        }),
        [page, perPage, search, trashedFilter, order]
    );

    const { isLoading, error, tags, meta } = useGetBlogTags(params);
    const columns = useMemo(() => BlogTagsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "وسوم المدونة", en: "Blog Tags" }}
            columns={columns}
            items={tags}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={[]}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, tags)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/blog/tags/create")}
            addButtonLabel={{ ar: "إضافة وسم", en: "Add Tag" }}
            page={page} perPage={perPage} order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <BlogTagsTableRow
                    {...params} key={params.item.id} item={params.item}
                    perms={perms} onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default BlogTagsTable;
